import { default as EChartsReact, default as ReactEcharts } from "echarts-for-react";
import ReactDomServer from "react-dom/server";
import _ from "lodash";
import { DateTime, Interval } from "luxon";
import { useMemo, useState } from "react";
import { hexColor } from "../../../../../enums/colors";
import { splitEventByHour } from "../../../../../lib/calculations";
import { useAppStore } from "../../../../../stores/RootStore";
import { Event, FShift } from "../../../../../types/types";
import { t } from "i18next";

// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "equipment_item_division_id"
// 12: "personnel_division_id"
// 13: "state_changed"
// 14: "status_changed"
// 15: "person_changed"

export function HourHeatMap({events, from, to, personId, shift, height = 60, width}: {events: Event[]; from: DateTime, to: DateTime, personId: number, shift: FShift; height: number; width: string | number;}) {
    const {theme} = useAppStore()
    const [ref, setRef] = useState<EChartsReact | null>();
    const [hours, setHours] = useState<any[]>([])
    // const [chartData, setChartData] = useState<(number | string)[][]>([])
    const start_date = DateTime.fromSQL(String(events[0][3]));
    const end_date = DateTime.fromSQL(String(events[events.length - 1][3]));
  
    const start = DateTime.fromObject({
      year: start_date.year,
      month: start_date.month,
      day: start_date.day,
      hour: shift.start_at.hour,
      minute: shift.start_at.minute,
      second: shift.start_at.second,
    });
  
    const end = DateTime.fromObject({
      year: end_date.year,
      month: end_date.month,
      day: end_date.day,
      hour: shift.end_at.hour,
      minute: shift.end_at.minute,
      second: shift.end_at.second,
    });
  

    const shiftDuration = end.toSeconds() - start.toSeconds();

    const chartData = useMemo(() => {
        let hours = Interval.fromDateTimes(shift.start_at, shift.end_at).splitBy({ hour: 1 }).map((date: Interval) => date.start?.hour);
        setHours(hours)
        let workedEvents = events.filter(e => e[8] === "worked" && Number(e[4]) >= from.toSeconds() && Number(e[6]) <= to.toSeconds() && Number(e[10]) === personId)
        let slicedEvents: Event[] = []
        workedEvents.map(e => splitEventByHour(e, slicedEvents))

        let forChart: (number | string)[][] = []

        hours.map((h, hi) => {
            let hourEvents = slicedEvents.filter(e => 
                DateTime.fromSQL(String(e[3])).hour === h
            )
            if(!hourEvents || hourEvents.length === 0) {
                forChart.push([
                    hi,
                    0,
                    0,
                    start_date.hour + h! > 24 ? end_date.set({hour: h}).startOf('hour').toFormat("dd.MM.yyyy HH:mm")! : start_date.set({hour: h}).startOf('hour').toFormat("dd.MM.yyyy HH:mm")!,
                    0
                ])
                return
            }

            forChart.push([
                hi,
                0,
                Math.round((_.sum(hourEvents.map(he => Number(he[7]))) / 3600) * 100),
                DateTime.fromSQL(String(hourEvents[0][1])).set({hour: h}).toFormat("dd.MM.yyyy HH:mm")!,
                Math.round((_.sum(hourEvents.map(he => Number(he[7]))) / 3600) * 100)
            ])
        })
        return forChart
    }, [events, from, to])

    const getOptions = () => ({
        anumation: false,
        tooltip: {
          trigger: "item",
          borderWidth: 0,
          padding: 0,
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params: {name: any; seriesName: string; value: (number)[] }) {
            return ReactDomServer.renderToStaticMarkup(<Tooltip params={params} />);
          }
        },
        toolbox: {
          show: false,
          left: 0,
          feature: {
            saveAsImage: {
              show: true,
              excludeComponents: ['toolbox', 'visualMap']
            },
          }
        },
        grid: {
          left: "0%",
          right: "0%",
          bottom: "0%",
          top: "0",
          containLabel: true,
          height: height,
        },
        xAxis: {
          type: 'category',
          position: 'top',
          data: hours,
          axisLabel: {
            formatter: function (val: number) {
              return `${val}:00`;
            },
          },
          splitArea: {
            show: true
          }
        },
        yAxis: {
          type: 'category',
          position: 'right',
          data: [0],
          axisLabel: {
            show: false,
            formatter: function (val: string) {
              return DateTime.fromSQL(val).toFormat("dd.MM");
            },
          },
          splitArea: {
            show: true
          }
        },
        visualMap: {
          show: false,
          min: 0,
          max: 100,
          calculable: true,
          orient: 'horizontal',
          left: 'right',
          top: '0%',
          inRange : {   
            color: theme == 'light' ? [hexColor.background, '#57a773', '#157145' ] : [hexColor.foreground, '#57a773', '#157145' ]//From smaller to bigger value ->
          }
        },
        series: [
          {
            name: 'Загрузка оборудования за',
            type: 'heatmap',
            data: chartData,
            label: {
              show: true,
              formatter: function (params: { marker: any; name: any; value: (number | string | undefined)[] }) {
                return params.value[2];
              },
            },
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
    });
    
    return (
        <ReactEcharts
            option={getOptions()}
            ref={(e) => { setRef(e);}}
            style={{ width: "100%", height: height + 20}}
            opts={{ renderer: "canvas" }}
        />
    )
}

const Tooltip = ({ params }: { params: { name: any; seriesName: string; value: (number | string)[] } }) => {
  let dateTime = DateTime.fromFormat(String(params.value[3]), "dd.MM.yyyy HH:mm")
  return (
    <div className="relative bg-background text-foreground text-sm p-2">
      <div className="flex w-32 justify-between"><span>{dateTime.toFormat("HH:mm")} - {dateTime.plus({hour: 1}).toFormat("HH:mm")}:</span> <span className="font-medium">{Number(params.value[2])}%</span></div>
    </div>
  );
};